<script setup lang="ts">
import FinestInputText from '@/components/FinestInputText.vue'
import PortfolioAddressCell from '@/components/portfolio/PortfolioAddressCell.vue'
import { toPortfolioRows, type PortfolioRow } from '@/components/portfolio/portfolioRows'
import { FilterMatchMode } from '@primevue/core/api'
import type { DataTableRowClickEvent } from 'primevue/datatable'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import EstateObjectData = App.Data.EstateObject.EstateObjectData
import EstateObjectType = App.Enums.EstateObject.EstateObjectType

const props = defineProps<{
  estateObjects: EstateObjectData[]
  selectedId: string | null
}>()

const emit = defineEmits<{
  select: [id: string]
}>()

const { t, n } = useI18n()

const typeLabel = (type: EstateObjectType | null | undefined): string =>
  type ? t(`enums.estateObjectType.${type}`) : t('empty')

const rows = computed<PortfolioRow[]>(() =>
  toPortfolioRows(props.estateObjects, {}, {}, { typeLabel, withoutOwner: t('withoutOwner') }),
)

const filters = ref({
  global: { value: null as string | null, matchMode: FilterMatchMode.CONTAINS },
})

const filteredRows = ref<PortfolioRow[]>(rows.value)

watch(rows, (next) => (filteredRows.value = next))

const formatArea = (value: number | null): string => (value === null ? t('empty') : `${n(value)} m²`)

const formatEuro = (value: number | null): string => (value === null ? t('empty') : n(value, 'euros'))

const select = (event: DataTableRowClickEvent): void => emit('select', (event.data as PortfolioRow).id)

/** The pick survives a step back, so the row it came from stays marked. */
const rowClass = (data: unknown): string => ((data as PortfolioRow).id === props.selectedId ? 'bg-info-subtle' : '')
</script>

<template>
  <div>
    <div class="mb-4 flex flex-wrap items-center gap-3">
      <FinestInputText
        v-model="filters.global.value"
        icon="pi pi-search"
        :placeholder="t('searchPlaceholder')"
        :aria-label="t('search')"
        class="min-w-55 flex-1 sm:max-w-90" />

      <span class="text-ink-3 ml-auto whitespace-nowrap">
        {{ t('resultCount', { shown: filteredRows.length, total: rows.length }) }}
      </span>
    </div>

    <div class="border-line bg-surface overflow-hidden rounded-xl border">
      <DataTable
        v-model:filters="filters"
        :value="rows"
        data-key="id"
        sort-mode="single"
        sort-field="addressLine"
        :sort-order="1"
        :global-filter-fields="['addressLine', 'zipCode', 'city', 'estateObjectTypeLabel']"
        :paginator="rows.length > 10"
        :rows="10"
        row-hover
        :row-class="rowClass"
        class="finest-table"
        @value-change="filteredRows = $event as PortfolioRow[]"
        @row-click="select">
        <template #empty>
          <p class="text-ink-3 py-8 text-center">{{ t('emptyResult') }}</p>
        </template>

        <Column field="addressLine" :header="t('columns.addressLine')" sortable>
          <template #body="{ data }">
            <PortfolioAddressCell :row="data as PortfolioRow" />
          </template>
        </Column>

        <Column
          field="estateObjectType"
          sort-field="estateObjectTypeLabel"
          :header="t('columns.estateObjectType')"
          sortable>
          <template #body="{ data }">
            <div class="max-w-40 truncate" :title="(data as PortfolioRow).estateObjectTypeLabel">
              {{ (data as PortfolioRow).estateObjectTypeLabel }}
            </div>
          </template>
        </Column>

        <Column field="units" :header="t('columns.units')" sortable class="text-right tabular-nums">
          <template #body="{ data }">{{ (data as PortfolioRow).units ?? t('empty') }}</template>
        </Column>

        <Column field="area" :header="t('columns.area')" sortable class="text-right tabular-nums">
          <template #body="{ data }">{{ formatArea((data as PortfolioRow).area) }}</template>
        </Column>

        <Column field="price" :header="t('columns.price')" sortable class="text-right tabular-nums">
          <template #body="{ data }">{{ formatEuro((data as PortfolioRow).price) }}</template>
        </Column>

        <Column class="text-right" :header="t('columns.select')">
          <template #body>
            <i class="pi pi-chevron-right text-ink-3 text-xs" aria-hidden="true" />
          </template>
        </Column>
      </DataTable>
    </div>
  </div>
</template>

<style scoped src="../finest-table.css"></style>

<i18n>
{
  "de": {
    "empty": "—",
    "emptyResult": "Keine Objekte gefunden. Passe die Suche an.",
    "withoutOwner": "Ohne Eigentümer",
    "search": "Suche",
    "searchPlaceholder": "Adresse oder Ort suchen …",
    "resultCount": "{shown} von {total} Objekten",
    "columns": {
      "addressLine": "Objekt",
      "estateObjectType": "Art",
      "units": "Einh.",
      "area": "Fläche",
      "price": "Kaufpreis",
      "select": "Auswählen"
    }
  },
  "en": {
    "empty": "—",
    "emptyResult": "No properties found. Adjust your search.",
    "withoutOwner": "Without owner",
    "search": "Search",
    "searchPlaceholder": "Search address or city …",
    "resultCount": "{shown} of {total} properties",
    "columns": {
      "addressLine": "Property",
      "estateObjectType": "Type",
      "units": "Units",
      "area": "Area",
      "price": "Purchase price",
      "select": "Select"
    }
  }
}
</i18n>
